<script setup lang="ts">
import type { Team } from '~/types'
import { useConfigStore } from '~/store/useConfigStore'
import { DropdownType } from '~/enums/dropdown-type'
import { StaticPage } from '~/enums/static-page'

interface Props {
  team: Team.Model
}

const props = defineProps<Props>()
const scheduleId = ref<string>()

const { seoMetaInput, headInput, name } = useTeam(props.team)
const configStore = useConfigStore()
const { websiteTitle } = storeToRefs(configStore)

const { sponsor } = useSponsor(StaticPage.Schedule, props.team)

useSeoMeta(seoMetaInput.value)
useHead(headInput.value)
useSchemaOrg([
  defineWebPage({
    name: `Schedule ${name.value} — ${websiteTitle.value}`,
  }),
])
</script>

<template>
  <div class="team-schedule-page">
    <ScheduleEventsNextGame
      v-if="scheduleId"
      :team="team"
      :schedule-id="scheduleId"
      class="team-schedule-page__next-game"
    />
    <Wrapper>
      <AdPlacement
        :team
        class="team-schedule-page__ad team-schedule-page__ad--start"
      />
      <div class="team-schedule-page__heading">
        <UIDynamicHeading
          level="2"
          as="h1"
          class="team-schedule-page__title"
        >
          Schedule
        </UIDynamicHeading>

        <UISponsor
          v-if="sponsor"
          v-bind="sponsor"
          class="team-schedule-page__sponsor"
        />

        <div class="team-schedule-page__dropdowns">
          <UISchedulesDropdown
            v-model="scheduleId"
            :type="DropdownType.TEAM"
            class="team-schedule-page__dropdown"
          />
          <UIWmtCalendar
            class="team-schedule-page__add-to-calendar"
            :team
          />
        </div>
      </div>
      <ScheduleEventsTeam
        v-if="scheduleId"
        :team="team"
        :schedule-id="scheduleId"
        class="team-schedule-page__list"
      />
    </Wrapper>
  </div>
</template>

<style scoped lang="scss">
.team-schedule-page {
  @apply mb-[40px];
  @apply md:mb-[60px];
  @apply xl:mb-[100px];

  &__heading {
    @apply flex items-center flex-wrap justify-between mb-[40px] bg-white;
    @apply lg:mb-[60px];
  }

  &__sponsor {
    @apply lg:ml-6 lg:mr-auto;
  }

  &:not(:has(.team-schedule-page__next-game)) {
    @apply pt-[30px];
    @apply md:pt-[50px];
  }

  &__dropdowns {
    @apply w-full flex items-center flex-col gap-[10px] mt-[30px];
    @apply md:mt-0 md:flex-row md:w-auto md:gap-[14px];
  }

  &__dropdown {
    @apply w-full;
    @apply md:w-auto;

    &:deep(.dropdown__select) {
      @apply max-sm:w-full;
    }
  }

  &__add-to-calendar {
    @apply m-0;

    &:deep(.button) {
      @apply max-sm:w-full;
    }
  }
}
</style>
